import { Button, Form, FormInstance, Image, Input } from "antd";
import { useEffect } from "react";
import { EditOutlined } from "@ant-design/icons";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import TextArea from "antd/es/input/TextArea";
import UploadFoodImage from "../../../lib/UploadFoodImage";
import UploadImage from "../../components/UploadImage";

interface WhyUsSectionProps {
  heading?: string;
  subtext?: string;
  image?: string;
}

interface Props {
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  form: FormInstance;
  whyUsData?: WhyUsSectionProps;
  loading?: boolean;
}

const WhyUsSection: React.FC<Props> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  whyUsData,
  loading,
}) => {
  useEffect(() => {
    if (whyUsData) {
      form.setFieldsValue({
        whyUs: {
          heading: whyUsData.heading || "",
          subtext: whyUsData.subtext || "",
          image: whyUsData.image ? [whyUsData.image] : [],
        },
      });
    } else {
      form.setFieldsValue({
        whyUs: {
          heading: "",
          subtext: "",
          image: [],
        },
      });
    }
  }, [whyUsData, form]);

  return (
    <div>
      {!isEditing ? (
        <>
          <div className="hidden sm:block rounded-[20px] overflow-hidden">
            <table className="min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Image</th>
                  <th className="py-3 px-4 text-left">Heading</th>
                  <th className="py-3 px-4 text-left">Subtext</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {whyUsData ? (
                  <tr className="bg-[#F9F9F9] border-b">
                    <td className="py-3 px-4">
                      {whyUsData.image ? (
                        <Image
                          src={whyUsData.image}
                          className="mt-2 w-full max-w-[150px] rounded-md border"
                          width={80}
                        />
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="py-3 px-4">{whyUsData.heading || "—"}</td>
                    <td className="py-3 px-4">{whyUsData.subtext || "—"}</td>
                    <td className="py-3 px-4 text-right text-primary">
                      <EditOutlined
                        onClick={() => setIsEditing(true)}
                        className="cursor-pointer text-[20px]"
                      />
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-6">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Section Data
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="sm:hidden space-y-3">
            {whyUsData ? (
              <div className="bg-[#F9F9F9] border rounded-lg p-4 space-y-2">
                <p>
                  <b className="text-primary">Heading:</b> 
                  <div>{whyUsData.heading}</div>
                </p>
                <p>
                  <b className="text-primary">Subtext:</b> 
                  <div>{whyUsData.subtext}</div>
                </p>
                <p>
                  <b className="text-primary">Image:</b>
                  <div>
                  {whyUsData.image ? (
                    <Image
                      src={whyUsData.image}
                      className="mt-2 w-full max-w-[240px] rounded-md border"
                      width={120}
                    />
                  ) : (
                    "—"
                  )}
                  </div>
                </p>
                <EditOutlined
                  onClick={() => setIsEditing(true)}
                  className="cursor-pointer text-[20px] text-primary"
                />
              </div>
            ) : (
              <div>
                <div className="text-center py-6">
                  <Button type="dashed" onClick={() => setIsEditing(true)}>
                    Add Data
                  </Button>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div>
          <div className="bg-[#F9F9F9] p-4 rounded-lg">
            <Form layout="vertical" form={form}>
              <Form.Item
                label="Heading"
                name={["whyUs", "heading"]}
                rules={[{ required: true, message: "Please enter heading" }]}
              >
                <Input placeholder="Heading" />
              </Form.Item>

              <Form.Item
                label="Subtext"
                name={["whyUs", "subtext"]}
                rules={[{ required: true, message: "Please enter subtext" }]}
              >
                <TextArea rows={3} placeholder="Enter subtext..." />
              </Form.Item>

              <Form.Item
                label="Image"
                name={["whyUs", "image"]}
                rules={[{ required: true, message: "Please upload image" }]}
              >
                <UploadImage listType="picture-card" />
              </Form.Item>
            </Form>
          </div>

          <div className="flex gap-3 mt-6">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper onClick={handleCancel}>Cancel</ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhyUsSection;
